import * as classnames from 'classnames'
import Color from 'color'
import { first, get, cloneDeep, uniqBy } from 'lodash'
import moment from 'moment'
import * as PropTypes from 'prop-types'
import React from 'react'
import { contextMenu } from 'react-contexify'
import { ViewerItem, MOVE_NONE } from '@teqplay/viewer'

import { getGridTime } from '../../../utils/general'
import {
  visitIsCompleted,
  visitIsTakingPlace,
  convertTerminalVisitToMovableVisit,
  separateVisitsWithUniqBollard,
  getMovementOptions
} from '../../../utils/terminalVisitUtils'
import AdditionalInformation from '../terminalVisit/additionalInformation/AdditionalInformation'
import { TERMINAL_VISIT_CONTEXT_MENU_ID } from '../terminalVisitContextMenu/TerminalVisitContextMenu'
import './ParallelTerminalVisit.scss'
import { userPermissionRoles } from '../../../utils/userPermissions'
import SlackTimePrior from '../terminalVisit/slackTimePrior/SlackTimePrior'
import SlackTimeAfter from '../terminalVisit/slackTimeAfter/SlackTimeAfter'
import { isValidTimestamp } from '../../../utils/formUtils'

const noop = () => {}
export default function ParallelTerminalVisit(props) {
  const {
    /** @type {TerminalVisit} */ terminalVisit,
    onSelect,
    isSelected,
    userRoles,
    isOverlapping,
    timeToScreen,
    bollardToScreen,
    displayFields,
    conflicts,

    zIndex,
    onLayerDown,
    setConflictModalDimensions,
    setConflictModalConflicts,
    onDrag,
    onVisitChange,
    quays
  } = props

  function handleShowConflicts(event) {
    setConflictModalDimensions(event.target.getBoundingClientRect())
    setConflictModalConflicts(uniqBy(conflicts, 'description'))
  }

  function handleHideConflicts() {
    setConflictModalDimensions(null)
    setConflictModalConflicts(null)
  }

  function handleLayerDown(event) {
    event.stopPropagation()
    event.preventDefault()

    onLayerDown(terminalVisit._id)
  }

  function handleMouseUpLayerDown(event) {
    event.stopPropagation()
    event.preventDefault()
  }

  function handleChangeTerminalVisit(itemChange) {
    onDrag(itemChange, true)
  }

  // all orders are assumed to have equal bollardStart, bollardEnd, slackPrior, slackAfter
  const firstOrder = first(terminalVisit.orders)
  const startBollard = firstOrder.bollardStart
  const endBollard = firstOrder.bollardEnd
  const isSeaShip = get(terminalVisit, 'ship.type') === 'sea-ship'
  const minOrderStart = Math.min(...terminalVisit.orders.map(order => order.cargoStart))
  const maxOrderEnd = Math.max(...terminalVisit.orders.map(order => order.cargoEnd))

  const terminalVisitItem = {
    _id: terminalVisit._id,
    yStart: new Date(minOrderStart),
    yEnd: new Date(maxOrderEnd),
    xStart: startBollard,
    xEnd: endBollard,
    referenceTerminalVisit: cloneDeep(terminalVisit) // FIXME: this is terribly inefficient. As soon as we have an immutable solution, this cloneDeep is redundant
  }

  const startTime = terminalVisitItem.yStart.getTime()
  const className = classnames('terminal-visit-parallel', 'terminal-visit-wrapper', {
    completed: visitIsCompleted(terminalVisit)
  })

  const orderConflictsInVisit = conflicts
    ? conflicts.filter(conflict => terminalVisit.orders.map(order => order.uuid).includes(conflict.orderUuid))
    : []

  const hasVisitConflicts = conflicts ? conflicts.length !== orderConflictsInVisit.length : false

  const borderClassName = classnames('terminal-visit-border', 'parallel', {
    conflicted: hasVisitConflicts,
    selected: isSelected,
    overlapping: isOverlapping,
    completed: visitIsCompleted(terminalVisit),
    active: visitIsTakingPlace(terminalVisit)
  })

  const indicatorsStyle = {
    top: timeToScreen(terminalVisitItem.yStart) + 5,
    left: bollardToScreen(terminalVisitItem.xStart) + 5,
    zIndex
  }

  const { planner: isPlanner, orderAdmin: isOrderAdmin } = userPermissionRoles(userRoles)

  const visitShipClassName = classnames('terminal-visit-wrapper', 'terminal-visit-border', {
    selected: isSelected,
    overlapping: isOverlapping,
    completed: visitIsCompleted(terminalVisit),
    active: visitIsTakingPlace(terminalVisit)
  })

  const visitShipstyle = {
    backgroundColor: Color('lightgrey').fade(0.3),
    borderColor: 'lightblue',
    zIndex: zIndex
  }

  const canUseContextMenu = isPlanner || isOrderAdmin

  const renderShipdata = separateVisitsWithUniqBollard(terminalVisit)

  return (
    <React.Fragment>
      {/* render the ship as background */}
      {renderShipdata.map((ship, i) => {
        const terminalVisit = convertTerminalVisitToMovableVisit(props.terminalVisit, ship)
        return (
          <ViewerItem
            key={ship.uuid + 'parallel' + i}
            item={terminalVisit}
            movement={MOVE_NONE}
            yUnitToScreen={timeToScreen}
            xUnitToScreen={bollardToScreen}
            className={visitShipClassName}
            style={visitShipstyle}
          />
        )
      })}

      <ViewerItem
        key={terminalVisit._id}
        item={terminalVisitItem}
        movement={getMovementOptions(terminalVisit, isPlanner)}
        yUnitToScreen={timeToScreen}
        xUnitToScreen={bollardToScreen}
        className={className}
        style={{ zIndex }}
        onChange={handleChangeTerminalVisit}
      >
        <>
          <div className={classnames('grouping-indicator', { visible: props.isPartOfGrouping })} />

          {renderShipdata.map(ship => {
            const terminalVisit = convertTerminalVisitToMovableVisit(props.terminalVisit, ship)

            // fake bollards, proportional to the amount of cargo
            const refOrder = terminalVisit.referenceOrder
            const orderStartBollard = refOrder.bollardStart
            const orderEndBollard = refOrder.bollardEnd
            const orderStartTime = refOrder.cargoStart
            const orderEndTime = refOrder.cargoEnd

            const top = timeToScreen(orderStartTime) - timeToScreen(startTime)
            const height = timeToScreen(orderEndTime) - timeToScreen(orderStartTime)
            const style = {
              top,
              height,
              left: bollardToScreen(orderStartBollard) - bollardToScreen(startBollard),
              width: bollardToScreen(orderEndBollard) - bollardToScreen(orderStartBollard),
              backgroundColor: 'transparent',
              zIndex: zIndex < 0 ? 1 : zIndex + 1
            }
            //render border seperated by follow up with different borders
            return <div key={refOrder.uuid} className={borderClassName} style={style} />
          })}

          {terminalVisit.orders.map(order => {
            // fake bollards, proportional to the amount of cargo
            const orderBollards = calculateParallelBollards(terminalVisit, order)
            const orderStartBollard = orderBollards.startBollard
            const orderEndBollard = orderBollards.endBollard
            const orderStartTime = order.cargoStart
            const orderEndTime = order.cargoEnd

            // const orderSlackTimeAfterHeight = showSlackAfter(terminalVisit)
            //   ? timeToScreen(orderEndTime + order.slackAfter) - timeToScreen(orderEndTime)
            //   : 0

            const backgroundColor = order.color
            const borderColor = Color(backgroundColor).darken(0.4).toString()

            const top = timeToScreen(orderStartTime) - timeToScreen(startTime)
            const height = timeToScreen(orderEndTime) - timeToScreen(orderStartTime)
            const style = {
              top,
              height,
              left: bollardToScreen(orderStartBollard) - bollardToScreen(startBollard),
              width: bollardToScreen(orderEndBollard) - bollardToScreen(orderStartBollard),
              backgroundColor,
              borderColor
            }

            return (
              <div key={order.uuid} className="ship-details-wrapper order-info" style={style}>
                <SlackTimePrior
                  startTime={orderStartTime}
                  slackTime={order.slackPrior}
                  timeToScreen={timeToScreen}
                  visitStatus={terminalVisit.status}
                />

                <SlackTimeAfter
                  endTime={orderEndTime}
                  slackTime={order.slackAfter}
                  timeToScreen={timeToScreen}
                  visitStatus={terminalVisit.status}
                />

                {/*<i className={`fa ${order.timeIsChained ? 'fa-link' : 'fa-unlink' } chain-indicator`} aria-hidden="true" />*/}
                <AdditionalInformation
                  terminalVisit={terminalVisit}
                  order={order}
                  displayFields={displayFields}
                  quays={quays}
                />
              </div>
            )
          })}

          <div className="terminal-visit-content">
            <div className="ship-details-wrapper ship-info">
              <div className="ship-info-inner">
                {isSeaShip ? <i className="fa fa-anchor sea-ship-indicator" aria-hidden="true" /> : null}
                {terminalVisit.ship.name ? terminalVisit.ship.name : `${terminalVisit.ship.length} m`}
              </div>
            </div>
          </div>
        </>
      </ViewerItem>

      {terminalVisit.orders.map((order, orderIndex) => {
        // render the orders with "fake" bollards such that the width is proportionally to the cargo amount
        const { startBollard, endBollard } = calculateParallelBollards(terminalVisit, order)

        function handleChangeOrder(itemChange) {
          const { updatedItem, draggedArea } = itemChange
          const { ataTime, atdTime } = itemChange.initialItem.referenceTerminalVisit

          if (isValidTimestamp(ataTime) || isValidTimestamp(atdTime)) {
            const { yStart, yEnd } = itemChange.initialItem

            const sailedItemChange = { ...itemChange, updatedItem: { ...itemChange.updatedItem, yStart, yEnd } }

            if (itemChange.draggedArea === 'center') {
              onDrag(sailedItemChange, true)
              return
            }
          }

          if (!isValidTimestamp(atdTime) && draggedArea === 'bottom') {
            // TODO: use some immutability library here instead of cloneDeep
            const updatedTerminalVisit = cloneDeep(terminalVisit)
            const minCargoEnd = order.cargoStart + (order.slackPrior + order.slackAfter)
            updatedTerminalVisit.orders[orderIndex].cargoEnd = Math.max(
              getGridTime(moment(updatedItem.yEnd)),
              minCargoEnd
            )

            onVisitChange(updatedTerminalVisit)
          }

          if (!isValidTimestamp(atdTime) && draggedArea === 'top') {
            // TODO: use some immutability library here instead of cloneDeep
            const updatedTerminalVisit = cloneDeep(terminalVisit)
            const maxCargoStart = order.cargoEnd - (order.slackPrior + order.slackAfter)
            updatedTerminalVisit.orders[orderIndex].cargoStart = Math.min(
              getGridTime(moment(updatedItem.yStart)),
              maxCargoStart
            )

            onVisitChange(updatedTerminalVisit)
          }

          if (!isValidTimestamp(atdTime) && draggedArea === 'center') {
            onDrag(itemChange)
          }
        }

        function handleSelect(event) {
          event.preventDefault()
          if (event.shiftKey || event.metaKey) {
            props.onSelectionToggle(terminalVisitItem)
          } else {
            onSelect(terminalVisit, order.uuid)
          }
        }

        function handleContextMenu(event) {
          event.preventDefault()

          contextMenu.show({
            id: TERMINAL_VISIT_CONTEXT_MENU_ID,
            event,
            props: {
              terminalVisit,
              order
            }
          })
        }

        const grabberItem = {
          _id: terminalVisit._id,
          yStart: new Date(order.cargoStart),
          yEnd: new Date(order.cargoEnd),
          xStart: startBollard,
          xEnd: endBollard,
          referenceTerminalVisit: cloneDeep(terminalVisit) // FIXME: this is terribly inefficient. As soon as we have an immutable solution, this cloneDeep is redundant
        }

        const grabberClassName = classnames('terminal-visit-grabber single-parallel-order', {
          completed: visitIsCompleted(terminalVisit),
          conflicted: hasVisitConflicts
            ? undefined
            : orderConflictsInVisit.map(conflict => conflict.orderUuid).includes(order.uuid)
        })

        return (
          <ViewerItem
            key={order.uuid}
            item={grabberItem}
            movement={getMovementOptions(terminalVisit, isPlanner)}
            yUnitToScreen={timeToScreen}
            xUnitToScreen={bollardToScreen}
            className={grabberClassName}
            style={{ zIndex }}
            onChange={handleChangeOrder}
          >
            <div
              className="terminal-visit-grabber-contents"
              onClick={handleSelect}
              onContextMenu={canUseContextMenu ? handleContextMenu : noop}
            />
          </ViewerItem>
        )
      })}

      <div
        key={terminalVisit._id + ':indicators'}
        className="terminal-visit-indicators parallel"
        style={indicatorsStyle}
      >
        {conflicts ? (
          <span
            className="conflict indicator fa fa-exclamation-triangle"
            onMouseEnter={handleShowConflicts}
            onMouseOut={handleHideConflicts}
          />
        ) : null}

        {
          // we won't have the combination of boardBoard with
          terminalVisit.boardBoard ? (
            <span className="indicator" title="Board-board operation">
              B
            </span>
          ) : null
        }

        {isOverlapping && hasVisitConflicts ? (
          <span
            className="indicator"
            onClick={handleLayerDown}
            onMouseUp={handleMouseUpLayerDown}
            title="Move this visit behind other overlapping visits"
          >
            <span className="fa fa-clone fa-rotate-225" />
          </span>
        ) : null}
      </div>
    </React.Fragment>
  )
}

ParallelTerminalVisit.propTypes = {
  terminalVisit: PropTypes.object.isRequired,
  conflicts: PropTypes.array, // array with the conflicts of all orders together
  isSelected: PropTypes.bool.isRequired,
  isOverlapping: PropTypes.bool.isRequired,
  userRoles: PropTypes.array.isRequired,

  zIndex: PropTypes.number.isRequired,
  onLayerDown: PropTypes.func.isRequired,
  setConflictModalDimensions: PropTypes.func.isRequired,
  setConflictModalConflicts: PropTypes.func.isRequired,

  timeToScreen: PropTypes.func.isRequired,
  bollardToScreen: PropTypes.func.isRequired,

  onDrag: PropTypes.func.isRequired,
  onVisitChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  displayFields: PropTypes.array.isRequired,

  onSelectionToggle: PropTypes.func.isRequired,
  isPartOfGrouping: PropTypes.bool,
  quays: PropTypes.array.isRequired
}

/**
 *
 * @param {TerminalVisit} terminalVisit
 * @param {Order} order
 * @return {{
 *   startBollard: number,
 *   endBollard: number,
 * }}
 */
function calculateParallelBollards(terminalVisit, order) {
  // Calculate min amount, should be at least one bollard
  const amountForOneBollard = (order.bollardEnd - order.bollardStart) / terminalVisit.parallelGroups

  return {
    startBollard: order.bollardStart + amountForOneBollard * order.parallelGroup,
    endBollard: order.bollardStart + amountForOneBollard * (order.parallelGroup + 1)
  }
}
